import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { RbacMe } from '../../models/rbac.model';

@Injectable({
  providedIn: 'root'
})
export class RbacService {

  private readonly STORAGE_KEY = 'rbac_me';

  private rbacSubject = new BehaviorSubject<RbacMe | null>(this.readFromStorage());
  rbac$ = this.rbacSubject.asObservable();

  // On a cold load, app.component.ts's own bootstrap check and every route guard
  // (Administration/Module/SuperAdmin) each independently see "no cached snapshot yet"
  // before any of them has resolved, and each would otherwise fire its own GET /rbac/me/.
  // Sharing the in-flight request means every caller in that window gets the same response.
  private pendingFetch: Observable<RbacMe> | null = null;

  constructor(private http: HttpClient) {}

  private readFromStorage(): RbacMe | null {
    const raw = sessionStorage.getItem(this.STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  fetchMe(): Observable<RbacMe> {
    if (this.pendingFetch) {
      return this.pendingFetch;
    }
    this.pendingFetch = this.http.get<RbacMe>(`${(environment as any).rbac_base_url}/rbac/me/`).pipe(
      tap((me) => {
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(me));
        this.rbacSubject.next(me);
      }),
      finalize(() => { this.pendingFetch = null; }),
      shareReplay(1)
    );
    return this.pendingFetch;
  }

  getSnapshot(): RbacMe | null {
    return this.rbacSubject.getValue();
  }

  isSuperAdmin(): boolean {
    return !!this.getSnapshot()?.is_super_admin;
  }

  hasRole(role: string): boolean {
    return !!this.getSnapshot()?.roles.includes(role);
  }

  isModuleEnabled(module: string): boolean {
    if (module === '*') {
      return true;
    }
    const me = this.getSnapshot();
    if (!me) {
      return false;
    }
    // Module gating applies to every role, including super admin — enabled_modules
    // is the single source of truth for what's accessible for the org in session.
    return me.enabled_modules.includes(module);
  }

  /** True only if every module in the list is enabled — used where a page depends on more than one module (e.g. the "admin" master switch plus a specific module). */
  hasAllModules(modules: string[]): boolean {
    return modules.every(module => this.isModuleEnabled(module));
  }

  hasPermission(permission: string): boolean {
    const me = this.getSnapshot();
    if (!me) {
      return false;
    }
    return me.is_super_admin || me.permissions.includes(permission);
  }

  /** True only if every permission in the list is held. */
  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.hasPermission(permission));
  }

  clear(): void {
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.rbacSubject.next(null);
  }
}
